/**
 * Page Direction (CEO only).
 * Tableau de bord financier : honoraires, encaissements, pipeline, top clients.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import HoldingFinancePanel from '../components/HoldingFinancePanel'; // eslint-disable-line no-unused-vars
import WorksMonthPlanningPanel from '../components/WorksMonthPlanningPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => (n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const fmtEUR = (n) => `${fmt(n)} €`;

const STATUS_LABELS = {
  a_facturer: { l: 'A facturer', bg: 'bg-amber-100', fg: 'text-amber-800' },
  facture:    { l: 'Facturé',    bg: 'bg-blue-100',  fg: 'text-blue-800' },
  encaisse:   { l: 'Encaissé',   bg: 'bg-emerald-100', fg: 'text-emerald-800' },
};

const OverdueBanner = ({ overdues, total }) => {
  if (!overdues || overdues.length === 0) return null;
  return (
    <div className="bg-red-50 border-l-4 border-red-500 rounded-r-xl p-4 flex items-start gap-3" data-testid="overdue-banner">
      <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
      <div className="flex-1">
        <h3 className="font-bold text-red-900">{overdues.length} encaissement(s) en retard — {fmtEUR(total)} HT à relancer</h3>
        <ul className="mt-2 text-sm text-red-800 space-y-0.5">
          {overdues.slice(0, 5).map(o => (
            <li key={o.project_id} className="flex items-center gap-2">
              <span className="font-semibold">{o.name}</span>
              <span className="text-red-600">— {o.client_name || 'Sans client'}</span>
              <span className="ml-auto px-2 py-0.5 rounded-full bg-red-200 text-red-900 text-[11px] font-bold">+{o.overdue_days}j</span>
              <span className="font-bold">{fmtEUR(o.honoraires_ht)} HT</span>
            </li>
          ))}
        </ul>
        {overdues.length > 5 && <p className="text-xs text-red-700 mt-1 italic">... et {overdues.length - 5} autre(s)</p>}
      </div>
    </div>
  );
};

const RECURRENCE_LABEL = { monthly: 'Mensuel', quarterly: 'Trimestriel', annual: 'Annuel', once: 'Ponctuel' };
const CATEGORY_LABEL = {
  local: 'Local / Coworking', materiel: 'Materiel', vehicule: 'Vehicule',
  abonnement: 'Abonnement / SaaS', autre: 'Autre',
};
const EXCEPTIONNELLE_CATEGORY_LABEL = {
  juridique: 'Juridique / Legal', materiel: 'Matériel / Achat', evenement: 'Évènement / Comm.',
  formation: 'Formation', impot: 'Impôt / Taxe', autre: 'Autre',
};
const FEE_CATEGORY_LABEL = {
  honoraires_manuels: '🏠 Honoraires (off-platform)',
  apport_affaires: '🤝 Apport d\'affaires',
  partage_agence: '🏢 Partage agence immo',
  partage_connexion: '🔗 Partage de connexion',
  commission_courtier: '💼 Commission courtier',
};
const FEE_CATEGORY_COLOR = {
  honoraires_manuels: 'bg-teal-100 text-teal-800',
  apport_affaires: 'bg-amber-100 text-amber-800',
  partage_agence: 'bg-rose-100 text-rose-800',
  partage_connexion: 'bg-purple-100 text-purple-800',
  commission_courtier: 'bg-blue-100 text-blue-800',
};

const BRANCH_LABEL = { bretagne: 'RealEase Bretagne', nord: 'RealEase Nord', consolide: 'Groupe (Bretagne + Nord)' };
const BRANCH_ACCENT = { bretagne: 'bg-[#13736D] text-white', nord: 'bg-[#272526] text-[#ECC19B]', consolide: 'bg-gradient-to-r from-[#13736D] to-[#272526] text-white' };

const BranchTabs = ({ selected, onChange, crossAccess }) => {
  if (!crossAccess) return null;
  const branches = [
    { value: 'consolide', label: 'Groupe consolidé', icon: '◉' },
    { value: 'bretagne', label: 'RealEase Bretagne', icon: '●' },
    { value: 'nord', label: 'RealEase Nord', icon: '●' },
  ];
  return (
    <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl w-fit" data-testid="branch-tabs">
      {branches.map(b => (
        <button
          key={b.value}
          onClick={() => onChange(b.value)}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
            selected === b.value
              ? `${BRANCH_ACCENT[b.value]} shadow-sm`
              : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid={`branch-tab-${b.value}`}
        >
          <span className="mr-1.5">{b.icon}</span>{b.label}
        </button>
      ))}
    </div>
  );
};

const ChargesCourantesPanel = ({ onChange, branch, isConsolidated }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ label: '', category: 'local', amount_ht: '', recurrence: 'monthly', period_start: '', period_end: '', is_active: true, notes: '', target_branch: 'bretagne' });
  const [editingId, setEditingId] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/ceo/charges-courantes`, { params: { branch } });
      setItems(r.data.items || []);
    } catch {} finally { setLoading(false); }
  }, [branch]);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const reset = () => {
    setForm({ label: '', category: 'local', amount_ht: '', recurrence: 'monthly', period_start: '', period_end: '', is_active: true, notes: '', target_branch: 'bretagne' });
    setEditingId(null); setShowForm(false);
  };

  const submit = async () => {
    if (!form.label.trim() || !form.amount_ht) return;
    try {
      const { target_branch, ...rest } = form;
      const payload = {
        ...rest,
        amount_ht: Number(form.amount_ht),
        period_start: form.period_start || null,
        period_end: form.period_end || null,
      };
      const branchToSend = isConsolidated ? target_branch : branch;
      if (editingId) await axios.put(`${API}/ceo/charges-courantes/${editingId}`, payload);
      else await axios.post(`${API}/ceo/charges-courantes`, { ...payload, branch: branchToSend });
      reset();
      fetchItems();
      onChange && onChange();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette charge ?')) return;
    await axios.delete(`${API}/ceo/charges-courantes/${id}`);
    fetchItems();
    onChange && onChange();
  };

  const startEdit = (c) => {
    setForm({
      label: c.label || '', category: c.category || 'local',
      amount_ht: c.amount_ht || '', recurrence: c.recurrence || 'monthly',
      period_start: c.period_start || '', period_end: c.period_end || '',
      is_active: c.is_active !== false, notes: c.notes || '',
    });
    setEditingId(c.charge_id); setShowForm(true);
  };

  const monthlyEq = (c) => {
    const a = Number(c.amount_ht) || 0;
    if (c.recurrence === 'monthly') return a;
    if (c.recurrence === 'quarterly') return a / 3;
    if (c.recurrence === 'annual') return a / 12;
    return 0;
  };
  const total_monthly_ht = items
    .filter(c => c.is_active && c.recurrence !== 'once')
    .reduce((s, c) => s + monthlyEq(c), 0);

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="charges-courantes-panel">
      <div className="flex items-center justify-between p-5 border-b border-gray-100">
        <div>
          <h3 className="font-semibold text-gray-900">Charges courantes entreprise</h3>
          <p className="text-xs text-gray-500">Local, matériel, véhicule, abonnements... — <b>{fmtEUR(total_monthly_ht)} HT / mois équivalent</b> (mensuel + trim/12 + annuel/12)</p>
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="text-sm px-3 py-1.5 bg-[#13736D] text-white rounded-lg hover:bg-[#0F5C57] font-semibold"
                  data-testid="add-charge-courante-btn">+ Ajouter</button>
        )}
      </div>

      {showForm && (
        <div className="p-4 bg-[#FDF8F0] border-b border-[#E8DCC4] space-y-2">
          {isConsolidated && !editingId && (
            <div className="flex items-center gap-2 mb-2">
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Branche cible :</label>
              <select value={form.target_branch} onChange={e => setForm({ ...form, target_branch: e.target.value })}
                      className="text-sm border border-[#13736D]/40 rounded px-2 py-1.5 bg-white" data-testid="charge-courante-branch">
                <option value="bretagne">RealEase Bretagne</option>
                <option value="nord">RealEase Nord</option>
              </select>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <input type="text" placeholder="Intitulé" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })}
                   className="text-sm border border-gray-200 rounded px-2 py-1.5" data-testid="charge-courante-label" />
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                    className="text-sm border border-gray-200 rounded px-2 py-1.5">
              {Object.entries(CATEGORY_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <input type="number" placeholder="Montant HT" value={form.amount_ht} onChange={e => setForm({ ...form, amount_ht: e.target.value })}
                   className="text-sm border border-gray-200 rounded px-2 py-1.5 text-right" data-testid="charge-courante-amount" />
            <select value={form.recurrence} onChange={e => setForm({ ...form, recurrence: e.target.value })}
                    className="text-sm border border-gray-200 rounded px-2 py-1.5">
              <option value="monthly">Mensuel</option>
              <option value="quarterly">Trimestriel</option>
              <option value="annual">Annuel</option>
              <option value="once">Ponctuel</option>
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-semibold text-gray-500 uppercase">Début (optionnel)</label>
              <input type="date" value={form.period_start} onChange={e => setForm({ ...form, period_start: e.target.value })}
                     className="w-full text-sm border border-gray-200 rounded px-2 py-1.5" />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-gray-500 uppercase">Fin (optionnelle — vide = en cours, ex. loyer)</label>
              <input type="date" value={form.period_end} onChange={e => setForm({ ...form, period_end: e.target.value })}
                     className="w-full text-sm border border-gray-200 rounded px-2 py-1.5" />
            </div>
          </div>
          <input type="text" placeholder="Notes (optionnel)" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                 className="w-full text-sm border border-gray-200 rounded px-2 py-1.5" />
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={reset} className="text-sm px-3 py-1 border border-gray-300 rounded hover:bg-gray-50">Annuler</button>
            <button onClick={submit} className="text-sm px-3 py-1 bg-[#13736D] text-white rounded font-semibold"
                    data-testid="charge-courante-submit">{editingId ? 'Modifier' : 'Créer'}</button>
          </div>
        </div>
      )}

      {loading ? <p className="p-5 text-gray-400 italic">Chargement...</p> :
        items.length === 0 ? <p className="p-5 text-sm text-gray-400 italic text-center">Aucune charge enregistrée. Ajoutez vos charges fixes (local, leasing, abonnements...).</p> :
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="p-3 text-left">Intitulé</th>
                <th className="p-3 text-left">Catégorie</th>
                <th className="p-3 text-right">Montant HT</th>
                <th className="p-3 text-right">TTC</th>
                <th className="p-3 text-center">Récurrence</th>
                <th className="p-3 text-center">Période</th>
                <th className="p-3 text-center">Actif</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(c => (
                <tr key={c.charge_id} className={`border-b border-gray-100 ${!c.is_active ? 'opacity-50' : ''}`}>
                  <td className="p-3">
                    <div className="font-medium text-gray-900">{c.label}</div>
                    {c.notes && <div className="text-xs text-gray-500">{c.notes}</div>}
                  </td>
                  <td className="p-3 text-xs text-gray-600">{CATEGORY_LABEL[c.category] || c.category || '—'}</td>
                  <td className="p-3 text-right font-semibold">{fmtEUR(c.amount_ht)}</td>
                  <td className="p-3 text-right text-xs text-gray-500">{fmtEUR(c.amount_ttc)}</td>
                  <td className="p-3 text-center text-xs">{RECURRENCE_LABEL[c.recurrence] || c.recurrence}</td>
                  <td className="p-3 text-center text-xs text-gray-500">
                    {c.period_start || '—'}{c.period_end ? ` → ${c.period_end}` : (c.recurrence === 'monthly' ? ' →' : '')}
                  </td>
                  <td className="p-3 text-center">
                    {c.is_active ? <span className="text-emerald-600 text-lg">●</span> : <span className="text-gray-300 text-lg">●</span>}
                  </td>
                  <td className="p-3 flex gap-1 justify-end">
                    <button onClick={() => startEdit(c)} className="text-[#13736D] hover:bg-[#13736D]/10 p-1 rounded" title="Modifier">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                    </button>
                    <button onClick={() => remove(c.charge_id)} className="text-red-500 hover:bg-red-50 p-1 rounded" title="Supprimer">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" /></svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
    </div>
  );
};

const ChargesExceptionnellesPanel = ({ onChange, branch, isConsolidated }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ label: '', category: 'autre', amount_ht: '', date: '', notes: '', target_branch: 'bretagne' });
  const [editingId, setEditingId] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/ceo/charges-exceptionnelles`, { params: { branch } });
      setItems(r.data.items || []);
    } catch {} finally { setLoading(false); }
  }, [branch]);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const reset = () => {
    setForm({ label: '', category: 'autre', amount_ht: '', date: '', notes: '', target_branch: 'bretagne' });
    setEditingId(null); setShowForm(false);
  };
  const submit = async () => {
    if (!form.label.trim() || !form.amount_ht) return;
    try {
      const { target_branch, ...rest } = form;
      const payload = {
        ...rest,
        amount_ht: Number(form.amount_ht),
        date: form.date || null,
      };
      const branchToSend = isConsolidated ? target_branch : branch;
      if (editingId) await axios.put(`${API}/ceo/charges-exceptionnelles/${editingId}`, payload);
      else await axios.post(`${API}/ceo/charges-exceptionnelles`, { ...payload, branch: branchToSend });
      reset();
      fetchItems();
      onChange && onChange();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Supprimer cette charge exceptionnelle ?')) return;
    await axios.delete(`${API}/ceo/charges-exceptionnelles/${id}`);
    fetchItems();
    onChange && onChange();
  };
  const startEdit = (c) => {
    setForm({ label: c.label || '', category: c.category || 'autre', amount_ht: c.amount_ht || '',
              date: c.date || '', notes: c.notes || '' });
    setEditingId(c.charge_id); setShowForm(true);
  };

  const total_ht = items.reduce((s, c) => s + (Number(c.amount_ht) || 0), 0);

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="charges-exceptionnelles-panel">
      <div className="flex items-center justify-between p-5 border-b border-gray-100">
        <div>
          <h3 className="font-semibold text-gray-900">Charges exceptionnelles</h3>
          <p className="text-xs text-gray-500">One-shot non-récurrents : amende, achat ponctuel, événement... — Total <b>{fmtEUR(total_ht)} HT</b></p>
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="text-sm px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-semibold"
                  data-testid="add-charge-exceptionnelle-btn">+ Ajouter</button>
        )}
      </div>
      {showForm && (
        <div className="p-4 bg-amber-50 border-b border-amber-200 space-y-2">
          {isConsolidated && !editingId && (
            <div className="flex items-center gap-2 mb-2">
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Branche cible :</label>
              <select value={form.target_branch} onChange={e => setForm({ ...form, target_branch: e.target.value })}
                      className="text-sm border border-amber-400 rounded px-2 py-1.5 bg-white" data-testid="charge-exc-branch">
                <option value="bretagne">RealEase Bretagne</option>
                <option value="nord">RealEase Nord</option>
              </select>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <input type="text" placeholder="Intitulé" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })}
                   className="text-sm border border-gray-200 rounded px-2 py-1.5" data-testid="charge-exc-label" />
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                    className="text-sm border border-gray-200 rounded px-2 py-1.5">
              {Object.entries(EXCEPTIONNELLE_CATEGORY_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <input type="number" placeholder="Montant HT" value={form.amount_ht} onChange={e => setForm({ ...form, amount_ht: e.target.value })}
                   className="text-sm border border-gray-200 rounded px-2 py-1.5 text-right" data-testid="charge-exc-amount" />
            <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
                   className="text-sm border border-gray-200 rounded px-2 py-1.5" />
          </div>
          <input type="text" placeholder="Notes (optionnel)" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                 className="w-full text-sm border border-gray-200 rounded px-2 py-1.5" />
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={reset} className="text-sm px-3 py-1 border border-gray-300 rounded hover:bg-gray-50">Annuler</button>
            <button onClick={submit} className="text-sm px-3 py-1 bg-amber-600 text-white rounded font-semibold"
                    data-testid="charge-exc-submit">{editingId ? 'Modifier' : 'Créer'}</button>
          </div>
        </div>
      )}
      {loading ? <p className="p-5 text-gray-400 italic">Chargement...</p> :
        items.length === 0 ? <p className="p-5 text-sm text-gray-400 italic text-center">Aucune charge exceptionnelle. Ajoutez vos coûts ponctuels (juridique, achat, événement).</p> :
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="p-3 text-left">Intitulé</th>
                <th className="p-3 text-left">Catégorie</th>
                <th className="p-3 text-center">Date</th>
                <th className="p-3 text-right">Montant HT</th>
                <th className="p-3 text-right">TTC</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(c => (
                <tr key={c.charge_id} className="border-b border-gray-100 hover:bg-amber-50/30">
                  <td className="p-3">
                    <div className="font-medium text-gray-900">{c.label}</div>
                    {c.notes && <div className="text-xs text-gray-500">{c.notes}</div>}
                    {isConsolidated && c.branch && <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400">[{c.branch}]</span>}
                  </td>
                  <td className="p-3 text-xs text-gray-600">{EXCEPTIONNELLE_CATEGORY_LABEL[c.category] || c.category || '—'}</td>
                  <td className="p-3 text-xs text-center text-gray-600">{c.date || '—'}</td>
                  <td className="p-3 text-right font-semibold text-amber-700">- {fmtEUR(c.amount_ht)}</td>
                  <td className="p-3 text-right text-xs text-gray-500">{fmtEUR(c.amount_ttc)}</td>
                  <td className="p-3 flex gap-1 justify-end">
                    <button onClick={() => startEdit(c)} className="text-amber-600 hover:bg-amber-50 p-1 rounded" title="Modifier">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                    </button>
                    <button onClick={() => remove(c.charge_id)} className="text-red-500 hover:bg-red-50 p-1 rounded" title="Supprimer">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" /></svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
    </div>
  );
};

const ManualFeesPanel = ({ onChange, branch, isConsolidated }) => {
  const [items, setItems] = useState([]);
  const [byCategory, setByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [projectsList, setProjectsList] = useState([]);
  const [form, setForm] = useState({
    label: '', category: 'honoraires_manuels', amount_ht: '', date: '',
    expected_payment_date: '', payment_received_date: '', payment_status: 'a_facturer',
    source: '', notes: '', project_id: '', target_branch: 'bretagne', works_amount_ttc: '',
  });
  const [editingId, setEditingId] = useState(null);
  const [filterCat, setFilterCat] = useState('all');

  const fetchItems = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/ceo/manual-fees`, { params: { branch } });
      setItems(r.data.items || []);
      setByCategory(r.data.by_category || {});
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [branch]);

  const fetchProjects = useCallback(async () => {
    try {
      const params = branch && branch !== 'consolidated' ? { branch } : {};
      const r = await axios.get(`${API}/projects`, { params });
      setProjectsList(Array.isArray(r.data) ? r.data : []);
    } catch { setProjectsList([]); }
  }, [branch]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { if (showForm) fetchProjects(); }, [showForm, fetchProjects]);

  const reset = () => {
    setForm({
      label: '', category: 'honoraires_manuels', amount_ht: '', date: '',
      expected_payment_date: '', payment_received_date: '', payment_status: 'a_facturer',
      source: '', notes: '', project_id: '', target_branch: 'bretagne', works_amount_ttc: '',
    });
    setEditingId(null); setShowForm(false);
  };
  const submit = async () => {
    if (!form.label.trim() || !form.amount_ht) return;
    try {
      const { target_branch, ...rest } = form;
      const payload = {
        ...rest,
        amount_ht: Number(form.amount_ht),
        date: form.date || null,
        expected_payment_date: form.expected_payment_date || null,
        payment_received_date: form.payment_received_date || null,
        project_id: form.project_id || null,
        works_amount_ttc: form.works_amount_ttc ? Number(form.works_amount_ttc) : null,
      };
      const branchToSend = isConsolidated ? target_branch : branch;
      if (editingId) await axios.put(`${API}/ceo/manual-fees/${editingId}`, payload);
      else await axios.post(`${API}/ceo/manual-fees`, { ...payload, branch: branchToSend });
      reset();
      fetchItems();
      onChange && onChange();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Supprimer cet honoraire ?')) return;
    await axios.delete(`${API}/ceo/manual-fees/${id}`);
    fetchItems();
    onChange && onChange();
  };
  const markEncaisse = async (fee) => {
    if (!window.confirm(`Marquer "${fee.label}" comme encaissé ce jour ?`)) return;
    try {
      await axios.put(`${API}/ceo/manual-fees/${fee.fee_id}`, {
        payment_status: 'encaisse',
        payment_received_date: new Date().toISOString().slice(0, 10),
      });
      fetchItems();
      onChange && onChange();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };
  const startEdit = (f) => {
    setForm({
      label: f.label || '', category: f.category || 'honoraires_manuels',
      amount_ht: f.amount_ht || '', date: f.date || '',
      expected_payment_date: f.expected_payment_date || '',
      payment_received_date: f.payment_received_date || '',
      payment_status: f.payment_status || 'a_facturer',
      source: f.source || '', notes: f.notes || '',
      project_id: f.project_id || '', works_amount_ttc: f.works_amount_ttc || '',
    });
    setEditingId(f.fee_id); setShowForm(true);
  };

  const filtered = filterCat === 'all' ? items : items.filter(f => f.category === filterCat);
  const total_ht = items.reduce((s, f) => s + (Number(f.amount_ht) || 0), 0);
  const total_encaisse = items.filter(f => f.payment_status === 'encaisse').reduce((s, f) => s + (Number(f.amount_ht) || 0), 0);

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="manual-fees-panel">
      <div className="p-5 border-b border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold text-gray-900">Honoraires manuels & commissions</h3>
            <p className="text-xs text-gray-500">Dossiers hors plateforme + commissions reçues — Total <b>{fmtEUR(total_ht)} HT</b> · Encaissé <b className="text-emerald-700">{fmtEUR(total_encaisse)}</b></p>
          </div>
          {!showForm && (
            <button onClick={() => setShowForm(true)} className="text-sm px-3 py-1.5 bg-[#13736D] text-white rounded-lg hover:bg-[#0F5C57] font-semibold"
                    data-testid="add-manual-fee-btn">+ Ajouter</button>
          )}
        </div>
        {/* KPI cards par catégorie */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {Object.entries(FEE_CATEGORY_LABEL).map(([k, lbl]) => {
            const data = byCategory[k] || { count: 0, amount_ht: 0, encaisse_ht: 0 };
            return (
              <button key={k} onClick={() => setFilterCat(filterCat === k ? 'all' : k)}
                      className={`p-3 rounded-lg border text-left transition-all ${filterCat === k ? 'border-[#13736D] bg-[#13736D]/5' : 'border-gray-200 hover:border-gray-300'}`}
                      data-testid={`fee-cat-${k}`}>
                <div className="text-[11px] font-semibold uppercase text-gray-500 truncate">{lbl}</div>
                <div className="text-lg font-bold text-gray-900 mt-1">{fmtEUR(data.amount_ht)}</div>
                <div className="text-[10px] text-gray-400">{data.count} ligne(s) · {fmtEUR(data.encaisse_ht)} encaissé</div>
              </button>
            );
          })}
        </div>
        {filterCat !== 'all' && (
          <div className="mt-2">
            <button onClick={() => setFilterCat('all')} className="text-xs text-[#13736D] hover:underline">× Retirer le filtre</button>
          </div>
        )}
      </div>

      {showForm && (
        <div className="p-4 bg-[#FDF8F0] border-b border-[#E8DCC4] space-y-2">
          {isConsolidated && !editingId && (
            <div className="flex items-center gap-2 mb-2">
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Branche cible :</label>
              <select value={form.target_branch} onChange={e => setForm({ ...form, target_branch: e.target.value })}
                      className="text-sm border border-[#13736D]/40 rounded px-2 py-1.5 bg-white" data-testid="manual-fee-branch">
                <option value="bretagne">RealEase Bretagne</option>
                <option value="nord">RealEase Nord</option>
              </select>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input type="text" placeholder="Intitulé / Référence dossier" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })}
                   className="text-sm border border-gray-200 rounded px-2 py-1.5" data-testid="manual-fee-label" />
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                    className="text-sm border border-gray-200 rounded px-2 py-1.5" data-testid="manual-fee-category">
              {Object.entries(FEE_CATEGORY_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          {form.category === 'apport_affaires' && (
            <div className="p-2.5 rounded-lg border border-amber-200 bg-amber-50 space-y-1">
              <label className="text-[10px] uppercase text-amber-700 font-semibold">Montant travaux TTC (calcul auto de la commission)</label>
              <input type="number" placeholder="Montant TTC des travaux" value={form.works_amount_ttc}
                     onChange={e => {
                       const ttc = e.target.value;
                       const commission = ttc ? (Number(ttc) / 1.2 * 0.05).toFixed(2) : '';
                       setForm({ ...form, works_amount_ttc: ttc, amount_ht: commission });
                     }}
                     className="w-full text-sm border border-amber-300 rounded px-2 py-1.5 text-right bg-white" data-testid="manual-fee-works-ttc" />
              <p className="text-[10px] text-amber-700">Commission apport = 5 % du HT travaux (TTC ÷ 1,20 × 5 %){form.works_amount_ttc ? ` → ${fmtEUR(Number(form.works_amount_ttc) / 1.2 * 0.05)} HT` : ''}. Confidentiel Direction — jamais visible client / banque / courtier.</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input type="number" placeholder="Montant HT" value={form.amount_ht} onChange={e => setForm({ ...form, amount_ht: e.target.value })}
                   className="text-sm border border-gray-200 rounded px-2 py-1.5 text-right" data-testid="manual-fee-amount" />
            <input type="text" placeholder="Source (courtier, apporteur...)" value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}
                   className="text-sm border border-gray-200 rounded px-2 py-1.5" />
            <select value={form.payment_status} onChange={e => setForm({ ...form, payment_status: e.target.value })}
                    className="text-sm border border-gray-200 rounded px-2 py-1.5">
              <option value="a_facturer">À facturer</option>
              <option value="facture">Facturé</option>
              <option value="encaisse">Encaissé</option>
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] uppercase text-gray-500 font-semibold">Date saisie</label>
              <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
                     className="w-full text-sm border border-gray-200 rounded px-2 py-1.5" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-gray-500 font-semibold">Encaissement prévu</label>
              <input type="date" value={form.expected_payment_date} onChange={e => setForm({ ...form, expected_payment_date: e.target.value })}
                     className="w-full text-sm border border-gray-200 rounded px-2 py-1.5" />
            </div>
            {form.payment_status === 'encaisse' && (
              <div>
                <label className="text-[10px] uppercase text-gray-500 font-semibold">Date encaissement</label>
                <input type="date" value={form.payment_received_date} onChange={e => setForm({ ...form, payment_received_date: e.target.value })}
                       className="w-full text-sm border border-gray-200 rounded px-2 py-1.5" />
              </div>
            )}
          </div>
          <input type="text" placeholder="Notes (optionnel)" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                 className="w-full text-sm border border-gray-200 rounded px-2 py-1.5" />
          <div>
            <label className="text-[10px] uppercase text-gray-500 font-semibold">Projet lié (optionnel)</label>
            <select value={form.project_id} onChange={e => setForm({ ...form, project_id: e.target.value })}
                    className="w-full text-sm border border-gray-200 rounded px-2 py-1.5"
                    data-testid="manual-fee-project">
              <option value="">— Aucun projet (honoraire libre) —</option>
              {projectsList.map(p => (
                <option key={p.project_id} value={p.project_id}>
                  {p.name || 'Projet sans nom'}{p.branch ? ` [${p.branch}]` : ''}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-gray-400 mt-0.5">Laissez vide pour un honoraire libre (off-platform / commission apporteur sans projet dédié).</p>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={reset} className="text-sm px-3 py-1 border border-gray-300 rounded hover:bg-gray-50">Annuler</button>
            <button onClick={submit} className="text-sm px-3 py-1 bg-[#13736D] text-white rounded font-semibold"
                    data-testid="manual-fee-submit">{editingId ? 'Modifier' : 'Créer'}</button>
          </div>
        </div>
      )}

      {loading ? <p className="p-5 text-gray-400 italic">Chargement...</p> :
        filtered.length === 0 ? <p className="p-5 text-sm text-gray-400 italic text-center">Aucun honoraire manuel ou commission. Ajoutez vos dossiers hors plateforme et commissions reçues (apport, courtier, partage).</p> :
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="p-3 text-left">Référence</th>
                <th className="p-3 text-left">Catégorie</th>
                <th className="p-3 text-left">Source</th>
                <th className="p-3 text-right">Montant HT</th>
                <th className="p-3 text-center">Statut</th>
                <th className="p-3 text-center">Date</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(f => {
                const st = STATUS_LABELS[f.payment_status] || STATUS_LABELS.a_facturer;
                return (
                  <tr key={f.fee_id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="p-3">
                      <div className="font-medium text-gray-900">{f.label}</div>
                      {f.project_name && (
                        <div className="text-xs text-[#13736D]">
                          <a href={`/projects/${f.project_id}`} className="hover:underline" data-testid={`fee-project-link-${f.fee_id}`}>↳ {f.project_name}</a>
                        </div>
                      )}
                      {f.notes && <div className="text-xs text-gray-500">{f.notes}</div>}
                      {isConsolidated && f.branch && <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400">[{f.branch}]</span>}
                    </td>
                    <td className="p-3">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${FEE_CATEGORY_COLOR[f.category] || 'bg-gray-100 text-gray-700'}`}>{FEE_CATEGORY_LABEL[f.category] || f.category}</span>
                    </td>
                    <td className="p-3 text-xs text-gray-600">{f.source || '—'}</td>
                    <td className="p-3 text-right font-semibold text-[#13736D]">+ {fmtEUR(f.amount_ht)}<div className="text-[10px] text-gray-400 font-normal">{fmtEUR(f.amount_ttc)} TTC</div></td>
                    <td className="p-3 text-center">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${st.bg} ${st.fg}`}>{st.l}</span>
                    </td>
                    <td className="p-3 text-xs text-center text-gray-600">
                      {f.payment_status === 'encaisse' ? (f.payment_received_date || f.date || '—') : (f.expected_payment_date || f.date || '—')}
                    </td>
                    <td className="p-3 flex gap-1 justify-end">
                      {f.payment_status !== 'encaisse' && (
                        <button onClick={() => markEncaisse(f)} className="text-emerald-600 hover:bg-emerald-50 p-1 rounded" title="Marquer encaissé"
                                data-testid={`mark-encaisse-${f.fee_id}`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        </button>
                      )}
                      <button onClick={() => startEdit(f)} className="text-[#13736D] hover:bg-[#13736D]/10 p-1 rounded" title="Modifier">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                      </button>
                      <button onClick={() => remove(f.fee_id)} className="text-red-500 hover:bg-red-50 p-1 rounded" title="Supprimer">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" /></svg>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      }
    </div>
  );
};

const ProfitLossPanel = ({ kpis }) => {
  const ca_proj = kpis.encaisse_projets_ytd_ht || 0;
  const ca_manuel = kpis.encaisse_manuel_ytd_ht || 0;
  const ca = kpis.encaisse_ytd_ht || 0;
  const ce = kpis.charges_externes_ytd_ht || 0;
  const cc = kpis.charges_courantes_ytd_ht || 0;
  const cex = kpis.charges_exceptionnelles_ytd_ht || 0;
  const marge = kpis.marge_nette_ytd_ht || 0;
  const margeColor = marge >= 0 ? 'text-emerald-700 bg-emerald-50' : 'text-red-700 bg-red-50';
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="pnl-panel">
      <div className="p-5 border-b border-gray-100">
        <h3 className="font-semibold text-gray-900">Compte de résultat simplifié — YTD</h3>
        <p className="text-xs text-gray-500">Du 1er janvier à aujourd'hui — basé sur les encaissements RÉELS (projets + honoraires manuels).</p>
      </div>
      <div className="p-5 space-y-1">
        <div className="flex items-center justify-between py-1.5">
          <span className="text-xs text-gray-600 pl-4">Honoraires projets encaissés</span>
          <span className="text-xs font-semibold text-emerald-600">+ {fmtEUR(ca_proj)}</span>
        </div>
        <div className="flex items-center justify-between py-1.5 border-b border-gray-100">
          <span className="text-xs text-gray-600 pl-4">Honoraires manuels & commissions encaissés</span>
          <span className="text-xs font-semibold text-emerald-600">+ {fmtEUR(ca_manuel)}</span>
        </div>
        <div className="flex items-center justify-between py-2 border-b-2 border-gray-200">
          <span className="text-sm font-bold text-gray-800">= Total encaissé YTD</span>
          <span className="text-base font-bold text-emerald-700">{fmtEUR(ca)}</span>
        </div>
        <div className="flex items-center justify-between py-2 border-b border-gray-100">
          <span className="text-sm font-medium text-gray-700">— Charges externes (par projet encaissé)</span>
          <span className="text-base font-bold text-red-600">- {fmtEUR(ce)}</span>
        </div>
        <div className="flex items-center justify-between py-2 border-b border-gray-100">
          <span className="text-sm font-medium text-gray-700">— Commission Julie (5 % des honoraires encaissés)</span>
          <span className="text-base font-bold text-red-600">- {fmtEUR(kpis.commission_julie_ytd_ht || 0)}</span>
        </div>
        <div className="flex items-center justify-between py-2 border-b border-gray-100">
          <span className="text-sm font-medium text-gray-700">— Charges courantes entreprise</span>
          <span className="text-base font-bold text-red-600">- {fmtEUR(cc)}</span>
        </div>
        <div className="flex items-center justify-between py-2 border-b border-gray-100">
          <span className="text-sm font-medium text-gray-700">— Charges exceptionnelles</span>
          <span className="text-base font-bold text-amber-700">- {fmtEUR(cex)}</span>
        </div>
        <div className={`flex items-center justify-between py-3 px-3 mt-2 rounded-lg ${margeColor}`}>
          <span className="text-sm font-bold uppercase">Marge nette HT</span>
          <span className="text-xl font-bold">{marge >= 0 ? '+ ' : ''}{fmtEUR(marge)}</span>
        </div>
      </div>
    </div>
  );
};

const ChargesExternesProjet = ({ project, onUpdate }) => {
  const [showFormCharge, setShowFormCharge] = useState(false);
  const [showFormExtra, setShowFormExtra] = useState(false);
  const [formCharge, setFormCharge] = useState({ label: '', amount_ht: '', date: '', category: 'apporteur', company: '' });
  const [formExtra, setFormExtra] = useState({ label: '', amount_ht: '', date: '', company: '', category: 'partenaire_travaux', payment_status: 'a_recevoir', paid_to: 'realease' });
  const [adding, setAdding] = useState(false);

  const submitCharge = async () => {
    if (!formCharge.label.trim() || !formCharge.amount_ht) return;
    setAdding(true);
    try {
      const r = await axios.post(`${API}/ceo/projects/${project.project_id}/charges-externes`, {
        label: formCharge.label.trim(),
        amount_ht: Number(formCharge.amount_ht),
        date: formCharge.date || null,
        category: formCharge.category,
        company: formCharge.company.trim() || null,
      });
      onUpdate(r.data);
      setFormCharge({ label: '', amount_ht: '', date: '', category: 'apporteur', company: '' });
      setShowFormCharge(false);
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    finally { setAdding(false); }
  };
  const submitExtra = async () => {
    if (!formExtra.label.trim() || !formExtra.amount_ht) return;
    setAdding(true);
    try {
      const r = await axios.post(`${API}/ceo/projects/${project.project_id}/extra-fees`, {
        label: formExtra.label.trim(),
        amount_ht: Number(formExtra.amount_ht),
        date: formExtra.date || null,
        company: formExtra.company.trim() || null,
        category: formExtra.category,
        payment_status: formExtra.payment_status,
        paid_to: formExtra.paid_to || 'realease',
      });
      onUpdate(r.data);
      setFormExtra({ label: '', amount_ht: '', date: '', company: '', category: 'partenaire_travaux', payment_status: 'a_recevoir', paid_to: 'realease' });
      setShowFormExtra(false);
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    finally { setAdding(false); }
  };
  const removeCharge = async (charge_id) => {
    if (!window.confirm('Supprimer cette charge / commission versee ?')) return;
    const r = await axios.delete(`${API}/ceo/projects/${project.project_id}/charges-externes/${charge_id}`);
    onUpdate(r.data);
  };
  const removeExtra = async (fee_id) => {
    if (!window.confirm('Supprimer cet honoraire complementaire ?')) return;
    const r = await axios.delete(`${API}/ceo/projects/${project.project_id}/extra-fees/${fee_id}`);
    onUpdate(r.data);
  };

  const fin = project.finance || {};
  const charges = fin.charges_externes || [];
  const extras = fin.extra_fees || [];
  const CAT_LBL = { apporteur: 'Apporteur d\'affaires', commission_charge_projet: 'Commission CdP', sous_traitant: 'Sous-traitant / artisan', autre: 'Autre' };
  const CAT_BG = { apporteur: 'bg-amber-100 text-amber-800', commission_charge_projet: 'bg-purple-100 text-purple-800', sous_traitant: 'bg-blue-100 text-blue-800', autre: 'bg-gray-100 text-gray-700' };
  const EXTRA_CAT_LBL = { partenaire_travaux: 'Partenaire travaux', courtier: 'Courtier', autre: 'Autre' };
  const STATUS_LBL = { recue: 'Reçue', a_recevoir: 'À recevoir' };
  const STATUS_BG = { recue: 'bg-emerald-100 text-emerald-800', a_recevoir: 'bg-amber-100 text-amber-800' };

  return (
    <div className="mt-3 space-y-3">
      {/* RECAP HONORAIRES GLOBAL */}
      <div className="bg-gradient-to-br from-[#0F5C57] to-[#13736D] text-white rounded-lg p-4 shadow-sm" data-testid={`finance-recap-${project.project_id}`}>
        <div className="text-[11px] uppercase font-semibold tracking-wide text-[#ECC19B] mb-2">Synthèse Honoraires RealEase</div>
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div>
            <div className="text-xs text-white/70">Honoraires principaux</div>
            <div className="text-lg font-bold">{fmtEUR(fin.honoraires_ht || 0)} HT</div>
            <div className="text-xs text-white/80">{fmtEUR(fin.honoraires_ttc || 0)} TTC</div>
          </div>
          <div>
            <div className="text-xs text-white/70">+ Commissions perçues</div>
            <div className="text-lg font-bold text-[#F4B083]">{fmtEUR(fin.extra_fees_total_ht || 0)} HT</div>
            <div className="text-xs text-white/80">{fmtEUR(fin.extra_fees_total_ttc || 0)} TTC</div>
          </div>
          <div className="border-l border-white/20 pl-3">
            <div className="text-xs text-white/70 uppercase">= Gain total</div>
            <div className="text-2xl font-bold">{fmtEUR(fin.honoraires_global_ht || 0)} HT</div>
            <div className="text-xs text-white/90 font-semibold">{fmtEUR(fin.honoraires_global_ttc || 0)} TTC</div>
          </div>
        </div>
        <div className="border-t border-white/15 mt-3 pt-3 grid grid-cols-2 gap-3 text-xs">
          <div>
            <span className="text-white/70">- Commissions versées (apporteurs) :</span>
            <span className="font-semibold ml-2">{fmtEUR(fin.apporteur_charges_ht || 0)} HT / {fmtEUR(fin.apporteur_charges_ttc || 0)} TTC</span>
          </div>
          <div>
            <span className="text-white/70">- Autres charges externes :</span>
            <span className="font-semibold ml-2">{fmtEUR(fin.other_charges_ht || 0)} HT / {fmtEUR(fin.other_charges_ttc || 0)} TTC</span>
          </div>
          <div className="col-span-2 pt-2 border-t border-white/15 flex items-center justify-between">
            <span className="uppercase font-bold text-[#ECC19B] tracking-wide">Marge brute projet</span>
            <span className="text-lg font-bold">{(fin.marge_brute_ht || 0) >= 0 ? '+ ' : ''}{fmtEUR(fin.marge_brute_ht || 0)} HT · {fmtEUR(fin.marge_brute_ttc || 0)} TTC</span>
          </div>
        </div>
      </div>

      {/* COMMISSIONS PERCUES (extras) */}
      <div className="bg-amber-50/60 rounded-lg p-3 border border-amber-200">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs uppercase font-semibold text-amber-900">Commissions / honoraires perçus en plus · Total {fmtEUR(fin.extra_fees_total_ht || 0)} HT</span>
          {!showFormExtra && (
            <button onClick={() => setShowFormExtra(true)} className="text-xs text-amber-700 hover:underline font-semibold"
                    data-testid={`add-extra-fee-${project.project_id}`}>+ Ajouter une commission perçue</button>
          )}
        </div>
        {showFormExtra && (
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-2">
            <input type="text" placeholder="Intitulé (ex: commission Plomberie Le Floch)" value={formExtra.label} onChange={e => setFormExtra({ ...formExtra, label: e.target.value })}
                   className="sm:col-span-4 text-xs border border-amber-200 rounded px-2 py-1.5" />
            <select value={formExtra.category} onChange={e => setFormExtra({ ...formExtra, category: e.target.value })}
                    className="sm:col-span-3 text-xs border border-amber-200 rounded px-2 py-1.5">
              <option value="partenaire_travaux">Partenaire travaux</option>
              <option value="courtier">Commission courtier</option>
              <option value="apport_affaires">Apport d'affaires</option>
              <option value="partage_agence">Partage agence immo</option>
              <option value="autre">Autre commission</option>
            </select>
            <input type="text" placeholder="Entreprise / payeur" value={formExtra.company} onChange={e => setFormExtra({ ...formExtra, company: e.target.value })}
                   className="sm:col-span-3 text-xs border border-amber-200 rounded px-2 py-1.5" />
            <input type="number" placeholder="HT" value={formExtra.amount_ht} onChange={e => setFormExtra({ ...formExtra, amount_ht: e.target.value })}
                   className="sm:col-span-2 text-xs border border-amber-200 rounded px-2 py-1.5 text-right" />
            <select value={formExtra.payment_status} onChange={e => setFormExtra({ ...formExtra, payment_status: e.target.value })}
                    className="sm:col-span-3 text-xs border border-amber-200 rounded px-2 py-1.5">
              <option value="a_recevoir">A recevoir (pas encore touchée)</option>
              <option value="recue">Reçue (déjà touchée)</option>
            </select>
            <input type="date" value={formExtra.date} onChange={e => setFormExtra({ ...formExtra, date: e.target.value })}
                   className="sm:col-span-2 text-xs border border-amber-200 rounded px-2 py-1.5" />
            <div className="sm:col-span-7 flex justify-end gap-2">
              <button onClick={submitExtra} disabled={adding} className="text-xs px-3 py-1 bg-amber-600 text-white rounded font-semibold disabled:opacity-50">Ajouter</button>
              <button onClick={() => { setShowFormExtra(false); setFormExtra({ label: '', amount_ht: '', date: '', company: '', category: 'partenaire_travaux', payment_status: 'a_recevoir', paid_to: 'realease' }); }} className="text-xs px-3 py-1 border rounded">Annuler</button>
            </div>
          </div>
        )}
        {extras.length === 0 ? (
          <p className="text-xs text-amber-700/60 italic">Aucune commission perçue sur ce projet.</p>
        ) : (
          <ul className="space-y-1">
            {extras.map(f => (
              <li key={f.fee_id} className="flex items-center gap-2 text-xs">
                <span className="text-gray-500 w-20">{f.date || '—'}</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${STATUS_BG[f.payment_status] || STATUS_BG.a_recevoir}`}>{STATUS_LBL[f.payment_status] || STATUS_LBL.a_recevoir}</span>
                {f.paid_to === 'holding' ? (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-900 text-amber-300" title="Encaisse par la Holding">HOLDING</span>
                ) : (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#008A8D]/15 text-[#006B6E]">REALEASE</span>
                )}
                <span className="text-[10px] text-gray-500">{EXTRA_CAT_LBL[f.category] || f.category}</span>
                <span className="flex-1 text-gray-800"><b>{f.label}</b>{f.company && <span className="ml-2 text-gray-500">— {f.company}</span>}</span>
                <span className="font-semibold text-amber-700">+ {fmtEUR(f.amount_ht)} HT / {fmtEUR(f.amount_ttc)} TTC</span>
                <button onClick={() => removeExtra(f.fee_id)} className="text-gray-300 hover:text-red-500" title="Supprimer">×</button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* CHARGES EXTERNES (commissions versees + autres) */}
      <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs uppercase font-semibold text-gray-600">Commissions versées & charges externes · Total {fmtEUR(fin.charges_externes_total_ht || 0)} HT</span>
          {!showFormCharge && (
            <button onClick={() => setShowFormCharge(true)} className="text-xs text-[#13736D] hover:underline font-semibold"
                    data-testid={`add-charge-externe-${project.project_id}`}>+ Ajouter une charge / commission versée</button>
          )}
        </div>
        {(fin.commission_julie_ht || 0) > 0 && (
          <div className="flex items-center gap-2 text-xs mb-2 pb-2 border-b border-gray-200" data-testid={`commission-julie-${project.project_id}`}>
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-teal-100 text-teal-800">AUTO</span>
            <span className="flex-1 text-gray-800"><b>Commission Julie</b> — 5 % des honoraires HT (auto-entrepreneuse, pas de TVA)</span>
            <span className="font-semibold text-red-600">- {fmtEUR(fin.commission_julie_ht)}</span>
          </div>
        )}
        {showFormCharge && (
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-2">
            <input type="text" placeholder="Intitulé" value={formCharge.label} onChange={e => setFormCharge({ ...formCharge, label: e.target.value })}
                   className="sm:col-span-4 text-xs border border-gray-200 rounded px-2 py-1.5" />
            <select value={formCharge.category} onChange={e => setFormCharge({ ...formCharge, category: e.target.value })}
                    className="sm:col-span-3 text-xs border border-gray-200 rounded px-2 py-1.5">
              <option value="apporteur">Apporteur d'affaires</option>
              <option value="commission_charge_projet">Commission charge de projet</option>
              <option value="sous_traitant">Sous-traitant / artisan</option>
              <option value="autre">Autre charge</option>
            </select>
            <input type="text" placeholder="Entreprise / bénéficiaire" value={formCharge.company} onChange={e => setFormCharge({ ...formCharge, company: e.target.value })}
                   className="sm:col-span-3 text-xs border border-gray-200 rounded px-2 py-1.5" />
            <input type="number" placeholder="HT" value={formCharge.amount_ht} onChange={e => setFormCharge({ ...formCharge, amount_ht: e.target.value })}
                   className="sm:col-span-1 text-xs border border-gray-200 rounded px-2 py-1.5 text-right" />
            <input type="date" value={formCharge.date} onChange={e => setFormCharge({ ...formCharge, date: e.target.value })}
                   className="sm:col-span-1 text-xs border border-gray-200 rounded px-2 py-1.5" />
            <div className="sm:col-span-12 flex justify-end gap-2">
              <button onClick={submitCharge} disabled={adding} className="text-xs px-3 py-1 bg-[#13736D] text-white rounded font-semibold disabled:opacity-50">Ajouter</button>
              <button onClick={() => { setShowFormCharge(false); setFormCharge({ label: '', amount_ht: '', date: '', category: 'apporteur', company: '' }); }} className="text-xs px-3 py-1 border rounded">Annuler</button>
            </div>
          </div>
        )}
        {charges.length === 0 ? (
          <p className="text-xs text-gray-400 italic">Aucune commission versée ou charge externe sur ce projet.</p>
        ) : (
          <ul className="space-y-1">
            {charges.map(c => (
              <li key={c.charge_id} className="flex items-center gap-2 text-xs">
                <span className="text-gray-500 w-20">{c.date || '—'}</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${CAT_BG[c.category] || CAT_BG.autre}`}>{CAT_LBL[c.category] || CAT_LBL.autre}</span>
                <span className="flex-1 text-gray-800"><b>{c.label}</b>{c.company && <span className="ml-2 text-gray-500">— {c.company}</span>}</span>
                <span className="font-semibold text-red-600">- {fmtEUR(c.amount_ht)} HT / {fmtEUR(c.amount_ttc)} TTC</span>
                <button onClick={() => removeCharge(c.charge_id)} className="text-gray-300 hover:text-red-500" title="Supprimer">×</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const KpiCard = ({ label, valueHt, valueTtc, accent = 'teal', sub = null, testid, isPercent = false, alert = false }) => {
  const accentMap = {
    teal: 'border-[#13736D] text-[#13736D]',
    amber: 'border-amber-500 text-amber-700',
    orange: 'border-orange-500 text-orange-700',
    blue: 'border-blue-500 text-blue-700',
    emerald: 'border-emerald-500 text-emerald-700',
    red: 'border-red-500 text-red-700',
    slate: 'border-slate-500 text-slate-700',
  };
  return (
    <div className={`bg-white rounded-xl border-l-4 ${accentMap[accent]} shadow-sm p-5 ${alert ? 'ring-2 ring-red-300' : ''}`} data-testid={testid}>
      <p className="text-xs uppercase font-semibold text-gray-500 tracking-wide">{label}</p>
      <p className={`text-3xl font-bold mt-2 ${accentMap[accent].split(' ')[1]}`}>
        {isPercent ? `${valueHt} %` : <>{fmtEUR(valueHt)} <span className="text-sm text-gray-400 font-normal">HT</span></>}
      </p>
      {valueTtc !== undefined && !isPercent && (
        <p className="text-sm text-gray-500 mt-1">{fmtEUR(valueTtc)} <span className="text-xs">TTC</span></p>
      )}
      {sub && <p className="text-xs text-gray-400 mt-2">{sub}</p>}
    </div>
  );
};

const PipelineBar = ({ monthly }) => {
  const max = Math.max(1, ...monthly.map(m => m.honoraires_ht || 0));
  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <h3 className="font-semibold text-gray-900 mb-4">Encaissements prévus — 6 prochains mois</h3>
      <div className="flex items-end gap-3 h-40">
        {monthly.map(m => {
          const h = max > 0 ? Math.max(4, (m.honoraires_ht / max) * 140) : 4;
          const [y, mm] = m.month.split('-');
          const label = new Date(parseInt(y), parseInt(mm) - 1, 1).toLocaleString('fr-FR', { month: 'short' });
          return (
            <div key={m.month} className="flex-1 flex flex-col items-center gap-1.5">
              <div className="text-[10px] font-semibold text-gray-600">{m.honoraires_ht > 0 ? fmt(m.honoraires_ht) : '-'}</div>
              <div className={`w-full rounded-t-lg transition-all ${m.honoraires_ht > 0 ? 'bg-[#13736D]' : 'bg-gray-100'}`}
                   style={{ height: `${h}px` }}></div>
              <div className="text-xs font-medium text-gray-700 uppercase">{label}</div>
              <div className="text-[10px] text-gray-400">{y.slice(2)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TopClients = ({ clients }) => (
  <div className="bg-white rounded-xl shadow-sm p-5">
    <h3 className="font-semibold text-gray-900 mb-4">Top 5 clients</h3>
    {clients.length === 0 ? (
      <p className="text-sm text-gray-400 italic">Aucune donnée pour le moment.</p>
    ) : (
      <div className="space-y-2">
        {clients.map((c, i) => (
          <div key={c.client} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#ECC19B] text-[#272526] text-xs font-bold flex items-center justify-center">{i + 1}</span>
              <span className="text-sm font-medium text-gray-800 truncate max-w-[180px]">{c.client}</span>
            </div>
            <div className="text-right">
              <div className="text-sm font-bold text-[#13736D]">{fmtEUR(c.honoraires_ht)} HT</div>
              <div className="text-xs text-gray-500">{fmtEUR(c.honoraires_ttc)} TTC</div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// eslint-disable-next-line no-unused-vars
const MONTH_NAMES_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const MONTH_SHORT_FR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

const MonthlyMilestones = ({ target, monthly, currentMonth }) => {
  if (!target || !monthly || monthly.length !== 12) return null;
  const milestone = target / 12;
  const maxVal = Math.max(milestone, ...monthly);
  const cumulActual = monthly.slice(0, currentMonth).reduce((a, b) => a + b, 0);
  const cumulExpected = milestone * currentMonth;
  const delta = cumulActual - cumulExpected;
  const onPace = delta >= 0;
  return (
    <div className="mt-4 pt-4 border-t border-gray-100" data-testid="monthly-milestones">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Jalons mensuels <span className="normal-case font-normal">(objectif ÷ 12 = {fmtEUR(milestone)} / mois)</span></p>
        {currentMonth > 0 && (
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${onPace ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-testid="milestone-pace">
            {onPace ? `✓ En avance de ${fmtEUR(delta)}` : `⚠ En retard de ${fmtEUR(-delta)}`} sur le cumul à fin {MONTH_SHORT_FR[currentMonth - 1]}
          </span>
        )}
      </div>
      <div className="relative">
        <div className="absolute left-0 right-0 border-t-2 border-dashed border-gray-300 z-10 pointer-events-none"
             style={{ bottom: `${18 + (milestone / maxVal) * 64}px` }} />
        <div className="grid grid-cols-12 gap-1.5 items-end">
          {monthly.map((val, i) => {
            const isPast = currentMonth > 0 && i < currentMonth;
            const hit = val >= milestone;
            const h = Math.max(val > 0 ? 4 : 2, (val / maxVal) * 64);
            let color = 'bg-gray-200';
            if (val > 0 || isPast) color = hit ? 'bg-emerald-500' : (isPast ? 'bg-amber-400' : 'bg-[#1FA39A]');
            return (
              <div key={i} className="flex flex-col items-center gap-1" title={`${MONTH_NAMES_FR[i]} : ${fmtEUR(val)} encaissé / jalon ${fmtEUR(milestone)}`} data-testid={`milestone-bar-${i + 1}`}>
                <span className="text-[9px] text-gray-500 font-medium leading-none">{val > 0 ? fmtEUR(val).replace(/\s?€/, '') : ''}</span>
                <div className="w-full flex items-end justify-center" style={{ height: '64px' }}>
                  <div className={`w-full max-w-[26px] rounded-t ${color} transition-all duration-500`} style={{ height: `${h}px` }} />
                </div>
                <span className={`text-[10px] leading-none ${i + 1 === currentMonth ? 'font-bold text-[#13736D]' : 'text-gray-400'}`}>{MONTH_SHORT_FR[i]}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex gap-4 mt-2 text-[10px] text-gray-400">
        <span><span className="inline-block w-2 h-2 rounded-sm bg-emerald-500 mr-1" />Jalon atteint</span>
        <span><span className="inline-block w-2 h-2 rounded-sm bg-amber-400 mr-1" />Sous le jalon (mois passé)</span>
        <span><span className="inline-block w-4 border-t-2 border-dashed border-gray-300 align-middle mr-1" style={{ display: 'inline-block' }} />Jalon mensuel</span>
      </div>
    </div>
  );
};

const AnnualTargetGauge = ({ branch, encaisseYtd }) => {
  const year = new Date().getFullYear();
  const [target, setTarget] = useState(0);
  const [monthly, setMonthly] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchTarget = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/ceo/annual-target`, { params: { branch, year } });
      setTarget(r.data.target_ht || 0);
      setMonthly(r.data.monthly_encaisse || []);
      setCurrentMonth(r.data.current_month || 0);
    } catch {} finally { setLoaded(true); }
  }, [branch, year]);
  useEffect(() => { fetchTarget(); }, [fetchTarget]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/ceo/annual-target`, { year, target_ht: Number(draft) || 0, branch });
      setTarget(Number(draft) || 0);
      setEditing(false);
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  if (!loaded) return null;
  const pct = target > 0 ? Math.min(100, ((encaisseYtd || 0) / target) * 100) : 0;
  const rawPct = target > 0 ? ((encaisseYtd || 0) / target) * 100 : 0;
  const remaining = Math.max(0, target - (encaisseYtd || 0));
  const reached = target > 0 && (encaisseYtd || 0) >= target;

  return (
    <div className="bg-white rounded-xl shadow-sm p-5" data-testid="annual-target-gauge">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-gray-900">🎯 Objectif CA {year}</h3>
          <p className="text-xs text-gray-500">CA encaissé YTD (projets + honoraires manuels) vs objectif annuel HT</p>
        </div>
        {editing ? (
          <div className="flex items-center gap-2">
            <input type="number" value={draft} onChange={e => setDraft(e.target.value)}
                   placeholder="Objectif HT (€)" autoFocus
                   className="text-sm border border-[#13736D]/40 rounded px-2 py-1.5 w-40 text-right"
                   data-testid="annual-target-input" />
            <button onClick={save} disabled={saving}
                    className="text-sm px-3 py-1.5 bg-[#13736D] text-white rounded-lg font-semibold hover:bg-[#0F5C57] disabled:opacity-50"
                    data-testid="annual-target-save">{saving ? '...' : 'Enregistrer'}</button>
            <button onClick={() => setEditing(false)} className="text-sm px-2 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50">Annuler</button>
          </div>
        ) : (
          <button onClick={() => { setDraft(target || ''); setEditing(true); }}
                  className="flex items-center gap-1.5 text-sm px-3 py-1.5 border border-gray-200 rounded-lg hover:border-[#13736D] hover:text-[#13736D] text-gray-600"
                  data-testid="annual-target-edit">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
            {target > 0 ? `Objectif : ${fmtEUR(target)} HT` : 'Définir un objectif'}
          </button>
        )}
      </div>
      {target > 0 ? (
        <div className="mt-4">
          <div className="flex items-end justify-between mb-1.5">
            <span className="text-2xl font-bold text-[#13736D]" data-testid="annual-target-encaisse">{fmtEUR(encaisseYtd || 0)} <span className="text-sm font-normal text-gray-400">HT encaissé</span></span>
            <span className={`text-sm font-bold ${reached ? 'text-emerald-600' : 'text-gray-700'}`} data-testid="annual-target-pct">{rawPct.toFixed(1)} %</span>
          </div>
          <div className="h-4 bg-gray-100 rounded-full overflow-hidden" data-testid="annual-target-bar">
            <div className={`h-full rounded-full transition-all duration-700 ${reached ? 'bg-gradient-to-r from-emerald-500 to-emerald-600' : 'bg-gradient-to-r from-[#13736D] to-[#1FA39A]'}`}
                 style={{ width: `${pct}%` }} />
          </div>
          <div className="flex justify-between mt-1.5 text-xs text-gray-500">
            <span>{reached ? '🏆 Objectif atteint — félicitations !' : `Reste ${fmtEUR(remaining)} HT à encaisser`}</span>
            <span>Objectif : <b>{fmtEUR(target)} HT</b></span>
          </div>
          <MonthlyMilestones target={target} monthly={monthly} currentMonth={currentMonth} />
        </div>
      ) : (
        <p className="mt-3 text-sm text-gray-400 italic" data-testid="annual-target-empty">Aucun objectif défini pour {year}. Cliquez sur « Définir un objectif » pour suivre votre progression.</p>
      )}
    </div>
  );
};

const JULIE_LINE_TYPE = {
  honoraires_projet: { l: 'Honoraires projet', cls: 'bg-teal-100 text-teal-800' },
  apport_travaux: { l: 'Apport travaux', cls: 'bg-amber-100 text-amber-800' },
};

const MONTH_FR = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
const fmtMonth = (ym) => {
  const [y, m] = ym.split('-');
  const name = MONTH_FR[parseInt(m, 10) - 1] || ym;
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${y}`;
};

const CommissionsHistoryPanel = ({ branch }) => {
  const [data, setData] = useState(null);
  const [openMonth, setOpenMonth] = useState(null);
  useEffect(() => {
    axios.get(`${API}/ceo/commissions-history`, { params: branch ? { branch } : {} })
      .then(r => { setData(r.data); setOpenMonth(r.data.months?.[0]?.month || null); })
      .catch(() => setData(null));
  }, [branch]);
  if (!data || !data.months?.length) return null;
  return (
    <div className="realease-card p-6" data-testid="commissions-history-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <h3 className="font-bold text-gray-900">Commissions d'apport encaissées</h3>
          <p className="text-xs text-gray-500">Historique par mois et par partenaire.</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-emerald-600" data-testid="commissions-history-total">{fmtEUR(data.total_ht)} HT</p>
          <p className="text-xs text-gray-400">{data.count} facture(s) encaissée(s)</p>
        </div>
      </div>
      <div className="space-y-2">
        {data.months.map(m => (
          <div key={m.month} className="border border-gray-100 rounded-lg overflow-hidden" data-testid={`commissions-history-month-${m.month}`}>
            <button onClick={() => setOpenMonth(openMonth === m.month ? null : m.month)}
                    className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors"
                    data-testid={`commissions-history-toggle-${m.month}`}>
              <span className="text-sm font-semibold text-gray-800">{fmtMonth(m.month)}</span>
              <span className="flex items-center gap-3">
                <span className="text-xs text-gray-400">{m.count} facture(s)</span>
                <span className="text-sm font-bold text-emerald-700">{fmtEUR(m.total_ht)} HT</span>
                <svg className={`w-4 h-4 text-gray-400 transition-transform ${openMonth === m.month ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </button>
            {openMonth === m.month && (
              <div className="px-4 pb-3 pt-1 bg-gray-50/60">
                {m.partners.map(pt => (
                  <div key={pt.partner_name} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0 text-sm"
                       data-testid={`commissions-history-partner-${m.month}-${pt.partner_name}`}>
                    <span className="text-gray-700">
                      {pt.partner_name}
                      <span className="text-[11px] text-gray-400 ml-1.5">
                        {pt.projects.map(pr => pr.name || pr.project_id).join(', ')}
                      </span>
                    </span>
                    <span className="font-semibold text-gray-900">{fmtEUR(pt.total_ht)} HT</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const CommissionsPendingPanel = ({ branch }) => {
  const [data, setData] = useState(null);
  const [marking, setMarking] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/ceo/commissions-pending`, { params: branch ? { branch } : {} })
      .then(r => setData(r.data)).catch(() => setData(null));
  }, [branch]);
  useEffect(() => { load(); }, [load]);
  const markPaid = async (it) => {
    if (!window.confirm(`Marquer la facture d'apport de « ${it.partner_name || '—'} » (projet ${it.name || it.project_id}) comme réglée ? Les relances automatiques s'arrêteront.`)) return;
    setMarking(it.invoice_id);
    try {
      await axios.put(`${API}/projects/${it.project_id}/commission-invoices/${it.invoice_id}`, { paid: true });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur lors du marquage');
    } finally {
      setMarking(null);
    }
  };
  if (!data || data.count === 0) return null;
  return (
    <div className="realease-card p-6" data-testid="commissions-pending-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <h3 className="font-bold text-gray-900">Factures d'apport en attente d'encaissement</h3>
          <p className="text-xs text-gray-500">Une ligne par partenaire facturé — relances auto J+10/20/30 puis tous les 10 j (partenaire + Amélie, + Jean dès J+30).</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-amber-600" data-testid="commissions-pending-total">{fmtEUR(data.total_pending_ht)} HT</p>
          <p className="text-xs text-gray-400">{data.count} facture(s) en attente</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-400 uppercase border-b border-gray-100">
              <th className="py-2 pr-3">Projet</th>
              <th className="py-2 pr-3">Partenaire</th>
              <th className="py-2 pr-3">Facture envoyée</th>
              <th className="py-2 pr-3">Montant HT</th>
              <th className="py-2 pr-3">Ancienneté</th>
              <th className="py-2 pr-3">Relances</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map(it => (
              <tr key={it.invoice_id} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`commission-pending-row-${it.invoice_id}`}>
                <td className="py-2 pr-3">
                  <Link to={`/projects/${it.project_id}`} className="font-semibold text-[#13736D] hover:underline">{it.name || it.project_id}</Link>
                </td>
                <td className="py-2 pr-3">
                  <span className="font-semibold text-gray-800">{it.partner_name || '—'}</span>
                  {it.partner_email
                    ? <span className="block text-[11px] text-gray-400">{it.partner_email}</span>
                    : <span className="block text-[11px] text-red-500 font-semibold">Email manquant</span>}
                </td>
                <td className="py-2 pr-3">
                  {it.commission_invoice_sent_at
                    ? new Date(it.commission_invoice_sent_at).toLocaleDateString('fr-FR')
                    : <span className="text-red-500 font-semibold text-xs">Non envoyée</span>}
                </td>
                <td className="py-2 pr-3 font-semibold">{it.commission_invoice_amount ? fmtEUR(it.commission_invoice_amount) : <span className="text-gray-400 text-xs">à renseigner</span>}</td>
                <td className="py-2 pr-3">
                  <span className={`text-xs font-bold ${it.days_since_deed > 30 ? 'text-red-600' : it.days_since_deed > 10 ? 'text-amber-600' : 'text-gray-500'}`}>
                    J+{it.days_since_deed}
                  </span>
                </td>
                <td className="py-2 pr-3 text-xs text-gray-500">{it.reminders_sent.length ? it.reminders_sent.map(o => `J+${o}`).join(', ') : '—'}</td>
                <td className="py-2 text-right">
                  <button
                    onClick={() => markPaid(it)}
                    disabled={marking === it.invoice_id}
                    className="text-xs px-3 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-50 whitespace-nowrap"
                    data-testid={`mark-commission-paid-${it.invoice_id}`}
                  >
                    {marking === it.invoice_id ? '...' : '✓ Réglée'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const JulieRecapPanel = ({ branch }) => {
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchRecap = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ceo/julie-recap`, { params: { branch, year: ym.y, month: ym.m } });
      setData(r.data);
    } catch {} finally { setLoading(false); }
  }, [branch, ym.y, ym.m]);
  useEffect(() => { fetchRecap(); }, [fetchRecap]);

  const nav = (delta) => setYm(prev => {
    let m = prev.m + delta, y = prev.y;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    return { y, m };
  });

  const items = data?.items || [];

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="julie-recap-panel">
      <div className="p-5 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-gray-900">🧾 Facture Julie — récap mensuel</h3>
          <p className="text-xs text-gray-500">5 % des honoraires HT encaissés + 5 % du HT des apports d'affaires travaux. Confidentiel Direction — jamais visible client / banque / courtier.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => nav(-1)} className="w-8 h-8 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 font-bold" data-testid="julie-recap-prev">‹</button>
          <span className="text-sm font-semibold text-gray-800 min-w-[140px] text-center" data-testid="julie-recap-month-label">{MONTH_NAMES_FR[ym.m - 1]} {ym.y}</span>
          <button onClick={() => nav(1)} className="w-8 h-8 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 font-bold" data-testid="julie-recap-next">›</button>
        </div>
      </div>

      {loading ? <p className="p-5 text-gray-400 italic">Chargement...</p> : !data ? null : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 bg-gray-50/50 border-b border-gray-100">
            <div className="p-3 rounded-lg bg-white border border-gray-200">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Total dû à Julie ce mois</p>
              <p className="text-xl font-bold text-red-600 mt-1" data-testid="julie-recap-total">{fmtEUR(data.total_commission_ht)}</p>
              <p className="text-[10px] text-gray-400">Auto-entrepreneuse — pas de TVA</p>
            </div>
            <div className="p-3 rounded-lg bg-white border border-gray-200">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-teal-700">Base honoraires encaissés</p>
              <p className="text-xl font-bold text-teal-800 mt-1">{fmtEUR(data.total_base_honoraires_ht)}</p>
              <p className="text-[10px] text-gray-400">→ {fmtEUR(data.total_base_honoraires_ht * 0.05)} de commission</p>
            </div>
            <div className="p-3 rounded-lg bg-white border border-gray-200">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">Base apports travaux encaissés</p>
              <p className="text-xl font-bold text-amber-800 mt-1">{fmtEUR(data.total_base_apport_ht)}</p>
              <p className="text-[10px] text-gray-400">→ {fmtEUR(data.total_base_apport_ht * 0.05)} de commission</p>
            </div>
          </div>

          {items.length === 0 ? (
            <p className="p-5 text-sm text-gray-400 italic text-center" data-testid="julie-recap-empty">Aucun encaissement sur {MONTH_NAMES_FR[ym.m - 1]} {ym.y} — rien à facturer pour Julie ce mois.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="p-3 text-left">Type</th>
                    <th className="p-3 text-left">Référence</th>
                    <th className="p-3 text-left">Client / Source</th>
                    <th className="p-3 text-center">Date encaissement</th>
                    <th className="p-3 text-right">Base HT</th>
                    <th className="p-3 text-right">Commission (5 %)</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => {
                    const t = JULIE_LINE_TYPE[it.type] || JULIE_LINE_TYPE.honoraires_projet;
                    return (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`julie-recap-row-${idx}`}>
                        <td className="p-3"><span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${t.cls}`}>{t.l}</span></td>
                        <td className="p-3 font-medium text-gray-900">{it.label}</td>
                        <td className="p-3 text-xs text-gray-600">{it.source || '—'}</td>
                        <td className="p-3 text-xs text-center text-gray-600">{it.date ? new Date(it.date).toLocaleDateString('fr-FR') : '—'}</td>
                        <td className="p-3 text-right text-gray-700">{fmtEUR(it.base_ht)}</td>
                        <td className="p-3 text-right font-bold text-red-600">{fmtEUR(it.commission_ht)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-red-50 font-semibold">
                  <tr>
                    <td className="p-3 text-right text-xs uppercase text-gray-600" colSpan={5}>Total à facturer par Julie — {MONTH_NAMES_FR[ym.m - 1]} {ym.y}</td>
                    <td className="p-3 text-right text-red-700 font-bold">{fmtEUR(data.total_commission_ht)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {(data.pending_commission_ht || 0) > 0 && (
            <div className="px-5 py-2.5 border-t border-gray-100 text-xs text-gray-500" data-testid="julie-recap-pending">
              ⏳ Commission à venir (facturé / à facturer, non encaissé) : <b className="text-gray-700">{fmtEUR(data.pending_commission_ht)}</b> — sera intégrée au récap du mois d'encaissement.
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ForecastHonorairesBlock = ({ kpis, forecastItems }) => {
  const [expanded, setExpanded] = useState(false);
  const hasAny = (kpis.forecast_under_6m_ht || 0) > 0
    || (kpis.forecast_current_year_ht || 0) > 0
    || (kpis.forecast_next_year_ht || 0) > 0
    || (kpis.forecast_later_ht || 0) > 0
    || (kpis.forecast_undated_ht || 0) > 0;
  if (!hasAny) return null;

  const BUCKET_LABEL = {
    under_6m: 'Moins de 6 mois',
    current_year: kpis.forecast_current_year_label || 'Annee en cours',
    next_year: kpis.forecast_next_year_label || 'Annee prochaine',
    later: 'Plus tard',
    undated: 'Sans date estimee',
  };
  const BUCKET_COLOR = {
    under_6m: 'bg-emerald-100 text-emerald-700',
    current_year: 'bg-teal-100 text-teal-700',
    next_year: 'bg-amber-100 text-amber-700',
    later: 'bg-slate-100 text-slate-700',
    undated: 'bg-gray-100 text-gray-700',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-5" data-testid="forecast-honoraires-block">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <svg className="w-5 h-5 text-[#13736D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Previsionnel d&apos;encaissement (Honoraires)
        </h3>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-[#13736D] hover:underline"
          data-testid="forecast-toggle-details"
        >
          {expanded ? 'Masquer le detail' : 'Voir le detail par projet'}
        </button>
      </div>
      <p className="text-[11px] text-gray-500 mb-3">
        Repartition basee sur la <b>date d&apos;encaissement prevue</b> de chaque projet (modifiable dans la finance projet). Honoraires HT.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="kpi-forecast-under-6m">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Moins de 6 mois</p>
          <p className="text-xl font-bold text-emerald-800 mt-1">{fmtEUR(kpis.forecast_under_6m_ht)}</p>
          <p className="text-[10px] text-emerald-600 mt-0.5">{fmtEUR(kpis.forecast_under_6m_ttc)} TTC</p>
        </div>
        <div className="p-3 rounded-lg bg-teal-50 border border-teal-200" data-testid="kpi-forecast-current-year">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-teal-700">{kpis.forecast_current_year_label || 'Annee en cours'}</p>
          <p className="text-xl font-bold text-teal-800 mt-1">{fmtEUR(kpis.forecast_current_year_ht)}</p>
          <p className="text-[10px] text-teal-600 mt-0.5">{fmtEUR(kpis.forecast_current_year_ttc)} TTC</p>
        </div>
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="kpi-forecast-next-year">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">{kpis.forecast_next_year_label || 'Annee prochaine'}</p>
          <p className="text-xl font-bold text-amber-800 mt-1">{fmtEUR(kpis.forecast_next_year_ht)}</p>
          <p className="text-[10px] text-amber-600 mt-0.5">{fmtEUR(kpis.forecast_next_year_ttc)} TTC</p>
        </div>
        <div className="p-3 rounded-lg bg-slate-50 border border-slate-200" data-testid="kpi-forecast-later">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-700">Plus tard</p>
          <p className="text-xl font-bold text-slate-800 mt-1">{fmtEUR(kpis.forecast_later_ht)}</p>
          <p className="text-[10px] text-slate-600 mt-0.5">Au-dela de l&apos;annee prochaine</p>
        </div>
        <div className="p-3 rounded-lg bg-gray-50 border border-gray-200" data-testid="kpi-forecast-undated">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-700">Sans date estimee</p>
          <p className="text-xl font-bold text-gray-800 mt-1">{fmtEUR(kpis.forecast_undated_ht)}</p>
          <p className="text-[10px] text-gray-600 mt-0.5">{kpis.forecast_undated_count || 0} projet(s) a dater</p>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 border-t border-gray-100 pt-4" data-testid="forecast-items-list">
          {forecastItems.length === 0 ? (
            <p className="text-xs italic text-gray-400">Aucun projet en attente d&apos;encaissement.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="forecast-items-table">
                <thead className="bg-gray-50 border-b border-gray-200 text-gray-600">
                  <tr>
                    <th className="text-left px-2 py-2">Projet</th>
                    <th className="text-left px-2 py-2">Client</th>
                    <th className="text-left px-2 py-2">Date prevue</th>
                    <th className="text-left px-2 py-2">Bucket</th>
                    <th className="text-right px-2 py-2">Honoraires HT</th>
                  </tr>
                </thead>
                <tbody>
                  {forecastItems.map((it) => (
                    <tr key={it.project_id} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`forecast-item-${it.project_id}`}>
                      <td className="px-2 py-2 font-medium text-gray-900">{it.name}</td>
                      <td className="px-2 py-2 text-gray-600">{it.client_name || '—'}</td>
                      <td className="px-2 py-2 text-gray-600">{it.expected_payment_date || <span className="italic text-gray-400">a definir</span>}</td>
                      <td className="px-2 py-2">
                        <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded font-semibold ${BUCKET_COLOR[it.bucket] || 'bg-gray-100 text-gray-700'}`}>
                          {BUCKET_LABEL[it.bucket] || it.bucket}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-right font-bold text-[#13736D]">{fmtEUR(it.honoraires_ht)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};



const FinanceRow = ({ project, onUpdate, isSelected, onToggleSelect }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(project.finance);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(project.finance); }, [project.finance]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        purchase_price: Number(form.purchase_price) || 0,
        works_cost: Number(form.works_cost) || 0,
        honoraires_ht: Number(form.honoraires_ht) || 0,
        expected_payment_date: form.expected_payment_date || null,
        payment_status: form.payment_status || 'a_facturer',
        payment_received_date: form.payment_received_date || null,
        progress_pct: form.progress_pct == null ? null : Number(form.progress_pct),
        notes_ceo: form.notes_ceo || null,
      };
      const r = await axios.put(`${API}/ceo/projects/${project.project_id}/finance`, payload);
      onUpdate(r.data);
      setEditing(false);
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const cancel = () => { setForm(project.finance); setEditing(false); };

  const f = project.finance;
  const status = STATUS_LABELS[f.payment_status] || STATUS_LABELS.a_facturer;
  const isOverdue = f.is_overdue;

  if (!editing) {
    return (
      <>
      <tr className={`border-b border-gray-100 hover:bg-gray-50 ${isOverdue ? 'bg-red-50/40' : ''} ${isSelected ? 'bg-red-50/60' : ''}`} data-testid={`finance-row-${project.project_id}`}>
        <td className="p-3 w-8">
          <input type="checkbox" checked={!!isSelected} onChange={onToggleSelect}
                 className="accent-[#13736D] cursor-pointer" data-testid={`select-finance-${project.project_id}`} />
        </td>
        <td className="p-3">
          <div className="font-semibold text-gray-900 text-sm flex items-center gap-2">
            {project.name || 'Sans nom'}
            {isOverdue && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500 text-white font-bold animate-pulse"
                    title={`Encaissement en retard de ${f.overdue_days} jours`}>
                Retard +{f.overdue_days}j
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500">{project.client_name || '—'}{project.city ? ` · ${project.city}` : ''}</div>
          {f.charges_externes && f.charges_externes.length > 0 && (
            <div className="text-[10px] text-gray-400 mt-1">
              {f.charges_externes.length} charge(s) externe(s) · marge brute {fmtEUR(f.marge_brute_ht)}
            </div>
          )}
        </td>
        <td className="p-3 text-sm text-right text-gray-700">{f.purchase_price ? fmtEUR(f.purchase_price) : '—'}</td>
        <td className="p-3 text-sm text-right text-gray-700">{f.works_cost ? fmtEUR(f.works_cost) : '—'}</td>
        <td className="p-3 text-right">
          <div className="text-sm font-bold text-[#13736D]">{fmtEUR(f.honoraires_ht)}</div>
          <div className="text-xs text-gray-500">{fmtEUR(f.honoraires_ttc)} TTC</div>
        </td>
        <td className="p-3 text-center">
          <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${status.bg} ${status.fg}`}>{status.l}</span>
        </td>
        <td className={`p-3 text-sm text-center ${isOverdue ? 'text-red-700 font-bold' : 'text-gray-600'}`}>
          {f.payment_status === 'encaisse' ? (f.payment_received_date || '—') : (f.expected_payment_date || '—')}
        </td>
        <td className="p-3 text-center">
          {f.progress_pct != null ? (
            <div className="flex flex-col items-center gap-1">
              <span className="text-xs font-semibold">{f.progress_pct}%</span>
              <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-[#13736D] transition-all" style={{ width: `${Math.min(100, f.progress_pct)}%` }}></div>
              </div>
            </div>
          ) : '—'}
        </td>
        <td className="p-3 text-center">
          <button onClick={() => setEditing(true)} className="text-[#13736D] hover:bg-[#13736D]/10 p-1 rounded"
                  data-testid={`edit-finance-${project.project_id}`} title="Modifier">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
          </button>
        </td>
      </tr>
      </>
    );
  }

  return (
    <>
    <tr className="border-b-2 border-[#13736D]/20 bg-[#FDF8F0]" data-testid={`finance-row-edit-${project.project_id}`}>
      <td className="p-3 w-8"></td>
      <td className="p-3 align-top">
        <div className="font-semibold text-gray-900 text-sm">{project.name}</div>
        <div className="text-xs text-gray-500">{project.client_name || '—'}</div>
        <textarea value={form.notes_ceo || ''} onChange={e => setForm({ ...form, notes_ceo: e.target.value })}
                  placeholder="Notes privées CEO..."
                  className="mt-2 w-full text-xs border border-gray-200 rounded p-1.5 h-14"
                  data-testid={`edit-notes-${project.project_id}`} />
      </td>
      <td className="p-3 align-top">
        <input type="number" value={form.purchase_price || ''} onChange={e => setForm({ ...form, purchase_price: e.target.value })}
               className="w-full text-sm border border-gray-200 rounded px-2 py-1 text-right" placeholder="0" />
      </td>
      <td className="p-3 align-top">
        <input type="number" value={form.works_cost || ''} onChange={e => setForm({ ...form, works_cost: e.target.value })}
               className="w-full text-sm border border-gray-200 rounded px-2 py-1 text-right" placeholder="0" />
      </td>
      <td className="p-3 align-top">
        {/* Iter 107 : saisie HT ET TTC, bidirectionnels (on stocke en HT en base) */}
        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-1">
            <span className="text-[9px] uppercase text-gray-500 font-semibold w-7">HT</span>
            <input
              type="number"
              value={form.honoraires_ht || ''}
              onChange={(e) => setForm({ ...form, honoraires_ht: e.target.value })}
              className="flex-1 text-sm border border-gray-200 rounded px-2 py-1 text-right font-bold"
              placeholder="0"
              data-testid={`edit-honoraires-${project.project_id}`}
            />
          </label>
          <label className="flex items-center gap-1">
            <span className="text-[9px] uppercase text-gray-500 font-semibold w-7">TTC</span>
            <input
              type="number"
              value={form.honoraires_ht ? (Number(form.honoraires_ht) * 1.2).toFixed(2) : ''}
              onChange={(e) => {
                const ttc = Number(e.target.value) || 0;
                setForm({ ...form, honoraires_ht: ttc > 0 ? +(ttc / 1.2).toFixed(2) : '' });
              }}
              className="flex-1 text-sm border border-gray-200 rounded px-2 py-1 text-right"
              placeholder="0"
              data-testid={`edit-honoraires-ttc-${project.project_id}`}
            />
          </label>
          <p className="text-[9px] text-gray-400 italic text-right">TVA 20%</p>
        </div>
      </td>
      <td className="p-3 align-top">
        <select value={form.payment_status || 'a_facturer'} onChange={e => setForm({ ...form, payment_status: e.target.value })}
                className="w-full text-xs border border-gray-200 rounded px-1 py-1"
                data-testid={`edit-status-${project.project_id}`}>
          <option value="a_facturer">A facturer</option>
          <option value="facture">Facturé</option>
          <option value="encaisse">Encaissé</option>
        </select>
      </td>
      <td className="p-3 align-top">
        <input type="date" value={form.expected_payment_date || ''} onChange={e => setForm({ ...form, expected_payment_date: e.target.value })}
               className="w-full text-xs border border-gray-200 rounded px-1 py-1" />
        {form.payment_status === 'encaisse' && (
          <input type="date" value={form.payment_received_date || ''} onChange={e => setForm({ ...form, payment_received_date: e.target.value })}
                 className="w-full text-xs border border-gray-200 rounded px-1 py-1 mt-1"
                 title="Date encaissement" />
        )}
      </td>
      <td className="p-3 align-top">
        <input type="number" min="0" max="100" value={form.progress_pct || ''} onChange={e => setForm({ ...form, progress_pct: e.target.value })}
               className="w-full text-sm border border-gray-200 rounded px-2 py-1 text-center" placeholder="%" />
      </td>
      <td className="p-3 align-top">
        <div className="flex flex-col gap-1">
          <button onClick={save} disabled={saving}
                  className="text-xs px-2 py-1 bg-[#13736D] text-white rounded hover:bg-[#0F5C57] disabled:opacity-50 font-semibold"
                  data-testid={`save-finance-${project.project_id}`}>
            {saving ? '...' : 'Enregistrer'}
          </button>
          <button onClick={cancel} disabled={saving}
                  className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50">
            Annuler
          </button>
        </div>
      </td>
    </tr>
    <tr className="bg-[#FDF8F0]/50">
      <td colSpan={8} className="px-3 pb-3">
        <ChargesExternesProjet project={project} onUpdate={onUpdate} />
      </td>
    </tr>
    </>
  );
};

export const CeoDashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState('all'); // all | a_facturer | facture | encaisse
  const [selected, setSelected] = useState([]);
  const [me, setMe] = useState(null);
  const [branch, setBranch] = useState('bretagne'); // bretagne | nord | consolide

  // Charge le profil utilisateur pour determiner si cross_branch_access
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/me`);
        setMe(r.data);
        // Si user a cross_access, vue par defaut = consolide
        if (r.data?.cross_branch_access) setBranch('consolide');
        else setBranch(r.data?.branch || 'bretagne');
      } catch {}
    })();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ceo/dashboard`, { params: { branch } });
      setData(r.data);
      setErr(null);
    } catch (e) { setErr(e.response?.data?.detail || 'Erreur'); }
    finally { setLoading(false); }
  }, [branch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const onProjectUpdate = (updated) => {
    setData(prev => prev ? {
      ...prev,
      projects: prev.projects.map(p => p.project_id === updated.project_id ? updated : p),
    } : prev);
    // Recalcul KPIs cote serveur
    fetchData();
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Chargement...</div>;
  if (err) return <div className="p-8 text-center text-red-600">{err}</div>;
  if (!data) return null;

  const { kpis, monthly_pipeline, top_clients, projects, branch_context } = data;
  const filtered = filter === 'all' ? projects : projects.filter(p => (p.finance?.payment_status || 'a_facturer') === filter);
  const toggleSelect = (pid) => setSelected(s => s.includes(pid) ? s.filter(x => x !== pid) : [...s, pid]);
  const allVisibleSelected = filtered.length > 0 && filtered.every(p => selected.includes(p.project_id));
  const toggleSelectAll = () => setSelected(allVisibleSelected ? [] : filtered.map(p => p.project_id));
  const excludeSelected = async () => {
    if (!window.confirm(`Retirer ${selected.length} dossier(s) du suivi financier ?\n\nIls disparaîtront du tableau et des KPI (À facturer, prévisionnel...) mais resteront visibles dans l'onglet Projets.`)) return;
    try {
      await axios.post(`${API}/ceo/projects/exclude`, { project_ids: selected, excluded: true });
      setSelected([]);
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };
  const restoreExcluded = async () => {
    if (!window.confirm('Restaurer tous les dossiers retirés du suivi financier ?')) return;
    try {
      await axios.post(`${API}/ceo/projects/exclude`, { restore_all: true });
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };
  const isConsolidated = branch_context?.selected === 'consolide';
  const crossAccess = !!me?.cross_branch_access;
  const branchLabelText = isConsolidated ? 'Vue Groupe (Bretagne + Nord)' : (BRANCH_LABEL[branch_context?.selected] || 'RealEase Bretagne');

  return (
    <div className="p-4 lg:p-8 space-y-6" data-testid="ceo-dashboard-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#272526] text-[#ECC19B] text-xs font-bold uppercase tracking-wider rounded-full mb-2">
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 1l3.09 6.26L22 8.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 13.14 2 8.27l6.91-1.01L12 1z" /></svg>
            Direction — {branchLabelText}
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Santé de l'entreprise</h1>
          <p className="text-sm text-gray-500">Honoraires, encaissements et prévisionnel — données strictement confidentielles.</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-400 uppercase">Au {new Date(kpis.today).toLocaleDateString('fr-FR')}</p>
          <p className="text-sm text-gray-700">{kpis.total_projects} projets {isConsolidated ? '(Groupe)' : `(${(BRANCH_LABEL[branch_context?.selected] || 'Bretagne').replace('RealEase ', '')})`}</p>
        </div>
      </div>

      {/* Onglets de branches (Jean uniquement) */}
      <BranchTabs selected={branch} onChange={setBranch} crossAccess={crossAccess} />

      {/* Bandeau alertes overdue */}
      <OverdueBanner overdues={data.overdue_projects} total={kpis.overdue_total_ht} />

      {/* Objectif CA annuel */}
      <AnnualTargetGauge branch={branch} encaisseYtd={kpis.encaisse_ytd_ht} />

      {/* KPIs principaux */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard label="Encaissé YTD" valueHt={kpis.encaisse_ytd_ht} valueTtc={kpis.encaisse_ytd_ttc} accent="emerald" testid="kpi-encaisse-ytd" />
        <KpiCard label="A facturer" valueHt={kpis.a_facturer_ht} valueTtc={kpis.a_facturer_ttc} accent="amber" sub="Statut « A facturer »" testid="kpi-a-facturer" />
        <KpiCard label="Facturé (en attente)" valueHt={kpis.facture_attente_ht} valueTtc={kpis.facture_attente_ttc} accent="orange" sub="Facturé, encaissement à venir" testid="kpi-facture-attente" />
        <KpiCard label="Pipeline 3 mois" valueHt={kpis.pipeline_3m_ht} valueTtc={kpis.pipeline_3m_ttc} accent="blue" sub="Confirmé sur les 92 prochains jours" testid="kpi-pipeline-3m" />
        <KpiCard label="Marge nette YTD" valueHt={kpis.marge_nette_ytd_ht} accent={kpis.marge_nette_ytd_ht >= 0 ? 'teal' : 'red'} sub="Encaissé − Charges externes − Commission Julie − Charges courantes" testid="kpi-marge-nette" />
      </div>

      {/* KPIs secondaires */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <KpiCard label="Charges externes YTD" valueHt={kpis.charges_externes_ytd_ht} accent="slate" sub="Cumulées sur tous les projets" testid="kpi-charges-externes" />
        <KpiCard label="Commission Julie (5 %)" valueHt={kpis.commission_julie_ytd_ht || 0} accent="slate" sub={`5 % honoraires + apports travaux HT encaissés · engagée : ${fmtEUR(kpis.commission_julie_engagee_ht || 0)}`} testid="kpi-commission-julie" />
        <KpiCard label="Charges courantes YTD" valueHt={kpis.charges_courantes_ytd_ht} accent="slate" sub="Local, leasing, abonnements..." testid="kpi-charges-courantes" />
        <KpiCard label="Charges exceptionnelles" valueHt={kpis.charges_exceptionnelles_ytd_ht} accent="amber" sub="Coûts ponctuels YTD" testid="kpi-charges-exceptionnelles" />
        <KpiCard label="Taux transformation" valueHt={kpis.taux_transformation_pct} accent="teal" sub={`% Encaissé / Facturé — TVA ${data.tva_pct}%`} testid="kpi-taux" isPercent />
      </div>

      {/* Pipeline + Top clients */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2"><PipelineBar monthly={monthly_pipeline} /></div>
        <div><TopClients clients={top_clients} /></div>
      </div>

      {/* Charges courantes + exceptionnelles (remontees au-dessus des honoraires) */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ChargesCourantesPanel onChange={fetchData} branch={branch} isConsolidated={isConsolidated} />
        <ChargesExceptionnellesPanel onChange={fetchData} branch={branch} isConsolidated={isConsolidated} />
      </div>

      {/* Tableau projets */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="font-semibold text-gray-900">Honoraires par projet</h3>
            <p className="text-xs text-gray-500">Cliquez sur le crayon pour éditer les montants et statuts.</p>
          </div>
          <div className="flex gap-2 items-center">
            {selected.length > 0 && (
              <button onClick={excludeSelected}
                      className="text-xs px-3 py-1.5 rounded-full font-semibold bg-red-600 text-white hover:bg-red-700"
                      data-testid="exclude-selected-btn">
                🗑 Retirer du suivi ({selected.length})
              </button>
            )}
            {['all', 'a_facturer', 'facture', 'encaisse'].map(s => (
              <button key={s} onClick={() => setFilter(s)}
                      className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-all ${filter === s ? 'bg-[#13736D] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                      data-testid={`filter-${s}`}>
                {s === 'all' ? 'Tous' : STATUS_LABELS[s]?.l}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="p-3 w-8">
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll}
                         className="accent-[#13736D] cursor-pointer" title="Tout sélectionner"
                         data-testid="select-all-finance" />
                </th>
                <th className="p-3 text-left">Projet</th>
                <th className="p-3 text-right">Prix bien</th>
                <th className="p-3 text-right">Coût travaux</th>
                <th className="p-3 text-right">Honoraires</th>
                <th className="p-3 text-center">Statut</th>
                <th className="p-3 text-center">Date</th>
                <th className="p-3 text-center">Avancement</th>
                <th className="p-3 text-center"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="p-8 text-center text-gray-400 italic">Aucun projet pour ce filtre.</td></tr>
              )}
              {filtered.map(p => <FinanceRow key={p.project_id} project={p} onUpdate={onProjectUpdate} isSelected={selected.includes(p.project_id)} onToggleSelect={() => toggleSelect(p.project_id)} />)}
            </tbody>
            {filtered.length > 0 && (
              <tfoot className="bg-[#13736D]/5 font-semibold">
                <tr>
                  <td className="p-3 text-right text-xs uppercase text-gray-600" colSpan={4}>Totaux visibles</td>
                  <td className="p-3 text-right text-[#13736D]">
                    {fmtEUR(filtered.reduce((s, p) => s + (p.finance?.honoraires_ht || 0), 0))} HT
                    <div className="text-xs text-gray-500 font-normal">
                      {fmtEUR(filtered.reduce((s, p) => s + (p.finance?.honoraires_ttc || 0), 0))} TTC
                    </div>
                  </td>
                  <td colSpan={4}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        {(data.excluded_count || 0) > 0 && (
          <div className="px-5 py-2.5 border-t border-gray-100 text-xs text-gray-500 flex items-center justify-between" data-testid="excluded-info">
            <span>{data.excluded_count} dossier(s) retiré(s) du suivi financier (toujours visibles dans l'onglet Projets)</span>
            <button onClick={restoreExcluded} className="text-[#13736D] font-semibold hover:underline" data-testid="restore-excluded-btn">Restaurer tout</button>
          </div>
        )}
      </div>

      {/* Compte de resultat + Lecture rapide */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ProfitLossPanel kpis={kpis} />
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="font-semibold text-gray-900 mb-3">Lecture rapide</h3>
          <ul className="text-sm text-gray-700 space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 mt-0.5">●</span>
              <span><b>Honoraires manuels & commissions</b> : ajoutez ci-dessous vos dossiers hors plateforme et les commissions reçues (apport d'affaires, partage de connexion, courtier).</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5">●</span>
              <span><b>Charges courantes</b> (mensuel/trimestriel/annuel) : YTD calculé automatiquement au prorata de chaque mois écoulé.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500 mt-0.5">●</span>
              <span><b>Charges exceptionnelles</b> : coûts ponctuels (juridique, achats, événement). N'apparaissent que dans le mois de leur date.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-0.5">●</span>
              <span><b>Alertes encaissement</b> : créées automatiquement chaque heure pour les projets dont la date d'encaissement prévue est dépassée.</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Honoraires manuels & commissions */}
      <ManualFeesPanel onChange={fetchData} branch={branch} isConsolidated={isConsolidated} />

      {/* Facture Julie - recap mensuel des commissions */}
      {/* Commissions d'apport en attente d'encaissement */}
      <CommissionsPendingPanel branch={branch} />

      <CommissionsHistoryPanel branch={branch} />

      <JulieRecapPanel branch={branch} />

      {/* Iter 100/102 : Planning travaux du mois - composant partage (couleur par projet) */}
      <WorksMonthPlanningPanel branch={branch} isConsolidated={isConsolidated} />

      {/* Mini-dashboard Partenaires & Apporteurs */}
      <PartnersPanel branch={branch} isConsolidated={isConsolidated} />

    </div>
  );
};

const PartnersPanel = ({ branch, isConsolidated }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCompany, setExpandedCompany] = useState(null);

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const params = isConsolidated ? {} : { branch };
      const r = await axios.get(`${API}/ceo/partners`, { params });
      setData(r.data);
    } catch {} finally { setLoading(false); }
  }, [branch, isConsolidated]);
  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-5 mt-4">
        <div className="text-sm text-gray-400 italic">Chargement des partenaires...</div>
      </div>
    );
  }
  if (!data || !data.partners.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-5 mt-4">
        <h3 className="text-lg font-bold text-gray-900 mb-2">Partenaires & Apporteurs</h3>
        <p className="text-sm text-gray-500 italic">Aucun partenaire avec commission enregistree. Ajoutez des commissions percues (entreprises travaux) ou versees (apporteurs d'affaires) dans les projets pour les voir apparaitre ici.</p>
      </div>
    );
  }
  const t = data.totals;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 mt-4" data-testid="partners-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Partenaires & Apporteurs</h3>
          <p className="text-xs text-gray-500">Vision consolidee par entreprise / personne. RealEase percoit (entreprises travaux) ou verse (apporteurs / CdP).</p>
        </div>
        <span className="text-xs text-gray-400">{t.partners_count} partenaire{t.partners_count > 1 ? 's' : ''}</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-bold">Commissions perçues</div>
          <div className="text-xl font-bold text-emerald-700 mt-1">+ {fmtEUR(t.total_received_ht)}</div>
          <div className="text-[10px] text-emerald-700/80 mt-1">HT cumule</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wide text-amber-700 font-bold">À toucher</div>
          <div className="text-xl font-bold text-amber-700 mt-1">{fmtEUR(t.total_received_a_recevoir_ht)}</div>
          <div className="text-[10px] text-amber-700/80 mt-1">HT à recevoir</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wide text-red-700 font-bold">Commissions versées</div>
          <div className="text-xl font-bold text-red-700 mt-1">- {fmtEUR(t.total_paid_ht)}</div>
          <div className="text-[10px] text-red-700/80 mt-1">HT verse (apporteurs + CdP)</div>
        </div>
        <div className="bg-[#0F5C57] text-white rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wide text-[#ECC19B] font-bold">Net RealEase</div>
          <div className="text-xl font-bold mt-1">{t.net_ht >= 0 ? '+ ' : ''}{fmtEUR(t.net_ht)}</div>
          <div className="text-[10px] text-white/70 mt-1">HT (perçu - versé)</div>
        </div>
      </div>

      <div className="overflow-x-auto border border-gray-100 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-[11px] uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Partenaire</th>
              <th className="text-center px-3 py-2 font-semibold">Type</th>
              <th className="text-right px-3 py-2 font-semibold">Perçu HT</th>
              <th className="text-right px-3 py-2 font-semibold">À toucher</th>
              <th className="text-right px-3 py-2 font-semibold">Versé HT</th>
              <th className="text-right px-3 py-2 font-semibold">Net HT</th>
              <th className="text-center px-3 py-2 font-semibold">Projets</th>
            </tr>
          </thead>
          <tbody>
            {data.partners.map(p => {
              const isExpanded = expandedCompany === p.company;
              const typeColor = p.type === 'perceiver' ? 'bg-emerald-100 text-emerald-800' : p.type === 'payee' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700';
              const typeLbl = p.type === 'perceiver' ? 'Apporteur RealEase' : p.type === 'payee' ? 'Apporteur d\'affaires' : 'Mixte';
              return (
                <React.Fragment key={p.company}>
                  <tr className={`border-t border-gray-100 ${isExpanded ? 'bg-[#FDF8F0]' : 'hover:bg-gray-50'} cursor-pointer`}
                      onClick={() => setExpandedCompany(isExpanded ? null : p.company)}
                      data-testid={`partner-row-${p.company.replace(/\s/g,'_')}`}>
                    <td className="px-3 py-2 font-semibold text-gray-900">{p.company}</td>
                    <td className="text-center"><span className={`text-[10px] font-bold px-2 py-0.5 rounded ${typeColor}`}>{typeLbl}</span></td>
                    <td className="text-right text-emerald-700 font-semibold">{p.received_ht > 0 ? `+ ${fmtEUR(p.received_ht)}` : '—'}</td>
                    <td className="text-right text-amber-700">{p.received_a_recevoir_ht > 0 ? fmtEUR(p.received_a_recevoir_ht) : '—'}</td>
                    <td className="text-right text-red-700 font-semibold">{p.paid_ht > 0 ? `- ${fmtEUR(p.paid_ht)}` : '—'}</td>
                    <td className={`text-right font-bold ${p.net_ht >= 0 ? 'text-[#0F5C57]' : 'text-red-700'}`}>{p.net_ht >= 0 ? '+ ' : ''}{fmtEUR(p.net_ht)}</td>
                    <td className="text-center text-gray-500">{p.projects_count}</td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-[#FDF8F0] border-t border-amber-100">
                      <td colSpan={7} className="px-3 py-3">
                        <div className="text-[11px] uppercase font-semibold text-gray-600 mb-2">Projets concernes</div>
                        <ul className="space-y-1">
                          {p.projects.map(proj => (
                            <li key={proj.project_id} className="flex items-center gap-3 text-xs">
                              <span className="flex-1 text-gray-800"><b>{proj.name}</b> <span className="text-gray-500">— {proj.client}</span></span>
                              {proj.received_ht > 0 && <span className="text-emerald-700 font-semibold">+ {fmtEUR(proj.received_ht)} HT</span>}
                              {proj.paid_ht > 0 && <span className="text-red-700 font-semibold">- {fmtEUR(proj.paid_ht)} HT</span>}
                            </li>
                          ))}
                        </ul>
                        {p.categories.length > 0 && (
                          <div className="mt-3 text-[10px] text-gray-500">
                            Categories : {p.categories.join(' · ')}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-gray-400 italic mt-3">Cliquez sur une ligne pour voir le detail des projets. "Apporteur RealEase" = entreprise/personne qui verse des commissions à RealEase (ex: entreprise travaux). "Apporteur d'affaires" = personne/entreprise à qui RealEase verse des commissions.</p>
    </div>
  );
};


export default CeoDashboardPage;
